import { useEffect, useState } from "react";
// utils
import axios from "../../utils/axios";
// hooks
import useAuth from "../../hooks/useAuth";
// @mui
import { Container, Box, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
// icons
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
// components
import CustomModal from "../../components/CustomModal";
//
import { useTranslation } from "react-i18next";
import moment from "moment";

// ----------------------------------------------------------------------

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

// ----------------------------------------------------------------------

export default function QuotationList() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successAlert, setSuccessAlert] = useState(null);
  const [errorAlert, setErrorAlert] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [quotationDeleteIntent, setQuotationDeleteIntent] = useState(null);
  const { t, i18n } = useTranslation();

  const adminColumns = [
    {
      field: "edit",
      headerName: t("tables.quotations.edit"),
      width: 100,
      renderCell: (params) => <AppRegistrationIcon />,
      cellClassName: "edit-cell",
    },
    {
      field: "delete",
      headerName: t("tables.quotations.delete"),
      width: 100,
      renderCell: (params) => <DeleteForeverIcon />,
      cellClassName: "delete-cell",
    },
    {
      field: "userFullname",
      headerName: t("tables.quotations.user_fullname"),
      width: 200,
      editable: false,
    },
    {
      field: "userEmail",
      headerName: t("tables.quotations.user_email"),
      width: 300,
      editable: false,
    },
    {
      field: "offer_number",
      headerName: t("tables.quotations.offer_number"),
      width: 250,
      editable: false,
    },
    {
      field: "client_info",
      headerName: t("tables.quotations.client_info"),
      width: 250,
      editable: false,
    },
    {
      field: "date",
      valueGetter: (params) => params.row.date,
      headerName: t("tables.quotations.date"),
      width: 150,
      renderCell: (params) => {
        return (
          <>
            {i18n.language === "it-IT"
              ? moment(params.value).format("DD-MM-YYYY")
              : params.value}
          </>
        );
      },
      editable: false,
    },
  ];

  const getQuotations = async () => {
    axios
      .post("/admin/quotations/list")
      .then((res) => {
        const quotations = res.data.quotations;
        // Get user email and update date format
        quotations.forEach((quotation) => {
          quotation.userEmail = quotation.user.email;
          quotation.userFullname = `${quotation.user.name} ${quotation.user.surname}`;
        });
        setQuotations(quotations);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setErrorAlert("Error retrieving quotations");
      });
  };

  useEffect(() => {
    getQuotations();
  }, []);

  const handleDeleteIntent = (quotation_id) => {
    setOpenDeleteModal(true);
    setQuotationDeleteIntent(quotation_id);
  };

  const handleModalRefuse = () => {
    setOpenDeleteModal(false);
    setQuotationDeleteIntent(null);
  };

  const handleDelete = async () => {
    setOpenDeleteModal(false);

    try {
      const res = await axios.post(
        `/quotations/delete/${quotationDeleteIntent}`
      );

      setSuccessAlert(t(res.data.message));
      setQuotationDeleteIntent(null);
      getQuotations();
      setTimeout(() => {
        setSuccessAlert(null);
      }, 2000);
    } catch (error) {
      console.error(error);

      setErrorAlert(t(error.message));
      setQuotationDeleteIntent(null);
      setTimeout(() => {
        setErrorAlert(null);
      }, 3000);
    }
  };

  return (
    <Container>
      <CustomModal
        style={modalStyle}
        open={openDeleteModal}
        handleNo={handleModalRefuse}
        handleYes={handleDelete}
        title={t("dialogs.delete_configuration.title")}
        yesLable="Sì"
        noLabel="No"
      />
      <Box
        sx={{
          height: 400,
          width: "100%",
          "& .edit-cell": {
            "&:hover": {
              cursor: "pointer",
            },
          },
          "& .delete-cell": {
            "&:hover": {
              cursor: "pointer",
            },
          },
        }}
      >
        {errorAlert && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{errorAlert}</Alert>
          </Box>
        )}
        {successAlert && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success">{successAlert}</Alert>
          </Box>
        )}

        <DataGrid
          rows={quotations}
          columns={adminColumns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          // checkboxSelection
          initialState={{
            // default sorting
            sorting: {
              sortModel: [{ field: "date", sort: "desc" }],
            },
          }}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          autoPageSize
          onCellClick={(cell) => {
            if (cell.field === "edit") {
              window.location.href = `/dashboard/planner/${cell.row.id}/modify`;
            } else if (cell.field === "delete") {
              handleDeleteIntent(cell.row.id);
            }
          }}
          sx={{
            "& .MuiDataGrid-menuIcon": {
              width: "auto",
              visibility: "visible",
            },
          }}
          localeText={t("datagrid", { returnObjects: true })} // https://github.com/mui/mui-x/blob/HEAD/packages/grid/x-data-grid/src/constants/localeTextConstants.ts
        />
      </Box>
    </Container>
  );
}
